import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { HashLink } from 'react-router-hash-link';
import logo from '../../images/LAKCHE.png';
import ContactModal from '../ContactModal';


const NavBar = () => {
  const { t, i18n } = useTranslation();
  const [top, setTop] = useState(!window.scrollY);
  const [isOpen, setIsOpen] = useState(false);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  // const [productOpen, setProductOpen] = useState(false);
  // const [mobileProductOpen, setMobileProductOpen] = useState(false);
  const [calendlyLoaded, setCalendlyLoaded] = useState(false);

  const toggleLanguage = () => {
    const newLang = i18n.language === 'en' ? 'fr' : 'en';
    i18n.changeLanguage(newLang);
  };

  const handleClick = () => {
    setIsOpen(!isOpen);
  };

  // Load Calendly script
  useEffect(() => {
    if (window.Calendly) {
      setCalendlyLoaded(true);
      return;
    }

    const link = document.createElement('link');
    link.href = 'https://assets.calendly.com/assets/external/widget.css';
    link.rel = 'stylesheet';
    document.head.appendChild(link);

    const script = document.createElement('script');
    script.src = 'https://assets.calendly.com/assets/external/widget.js';
    script.async = true;
    script.onload = () => setCalendlyLoaded(true);
    document.body.appendChild(script);

    return () => {
      if (document.head.contains(link)) document.head.removeChild(link);
      if (document.body.contains(script)) document.body.removeChild(script);
    };
  }, []);

  const CALENDLY_URL = 'https://calendly.com/dev-lakche/30min';

  const openCalendly = () => {
    if (window.Calendly) {
      window.Calendly.initPopupWidget({
        url: CALENDLY_URL,
      });
    } else {
      alert('Please wait a moment and try again. Loading calendar...');
    }
  };

  // Scroll handler
  useEffect(() => {
    const scrollHandler = () => {
      window.pageYOffset > 10 ? setTop(false) : setTop(true);
    };
    window.addEventListener('scroll', scrollHandler);
    return () => window.removeEventListener('scroll', scrollHandler);
  }, []);

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (isOpen && !e.target.closest('nav')) {
        setIsOpen(false);
      }
    };
    document.addEventListener('click', handleOutsideClick);
    return () => document.removeEventListener('click', handleOutsideClick);
  }, [isOpen]);

  return (
    <>
      <nav
        className={`fixed top-0 w-full z-50 transition-all duration-300 ease-in-out ${
          !top ? 'bg-white shadow-lg' : 'bg-transparent'
        }`}
      >
        <div className="flex flex-row justify-between items-center py-3 md:py-4 px-4 md:px-12">
          
          {/* Logo */}
          <div className="flex flex-row justify-center items-center text-center font-semibold">
            <HashLink 
              smooth 
              to="/" 
              className="transition-transform duration-200 hover:scale-105"
            >
              <div className="transition-colors duration-300">
                <img
                  src={logo}
                  alt="Lak-Che logo"
                  className={`h-8 md:h-9 object-contain transition-all duration-300 ${
                    !top ? '' : 'brightness-0 invert'
                  }`}
                />
              </div>
            </HashLink>
          </div>
          
          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-6">
            <HashLink
              smooth
              to="/#about"
              className={`px-2 font-extrabold transition-colors duration-300 ${
                !top ? 'text-gray-500 hover:text-blue-900' : 'text-white/90 hover:text-white'
              }`}
            >
              {t('nav.about')}
            </HashLink>

            <HashLink
              smooth
              to="/#services"
              className={`px-2 font-extrabold transition-colors duration-300 ${
                !top ? 'text-gray-500 hover:text-blue-900' : 'text-white/90 hover:text-white'
              }`}
            >
              {t('nav.services')}
            </HashLink>

            <HashLink
              smooth
              to="/security"
              className={`px-2 font-extrabold transition-colors duration-300 ${
                !top ? 'text-gray-500 hover:text-blue-900' : 'text-white/90 hover:text-white'
              }`}
            >
              {t('nav.security')}
            </HashLink>

            {/* Desktop Product Dropdown */}
            {/* <div
              className="relative"
              onMouseEnter={() => setProductOpen(true)}
              onMouseLeave={() => setProductOpen(false)}
            >
              <button
                className={`px-2 font-extrabold flex items-center gap-1 transition-colors duration-300 ${
                  !top ? 'text-gray-500 hover:text-blue-900' : 'text-white/90 hover:text-white'
                }`}
              >
                {t('nav.product')}
                <svg
                  className={`w-4 h-4 transition-transform duration-300 ${
                    productOpen ? 'rotate-180' : ''
                  }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {productOpen && (
                <div className="absolute top-full mt-4 w-80 bg-white rounded-2xl shadow-2xl border border-gray-100 py-2 z-50 overflow-hidden">
                  <a href="/software-solutions" className="group flex flex-col px-6 py-4 hover:bg-blue-50 transition">
                    <span className="font-extrabold text-gray-800 group-hover:text-blue-900">Website & App Creation</span>
                    <span className="text-sm text-gray-500">Modern web and mobile solutions tailored to your business</span>
                  </a>
                  <a href="/ai-solutions" className="group flex flex-col px-6 py-4 hover:bg-blue-50 transition">
                    <span className="font-extrabold text-gray-800 group-hover:text-blue-900">Custom AI Solutions</span>
                    <span className="text-sm text-gray-500">Intelligent systems built to solve real-world problems</span>
                  </a>
                  <a href="/blockchain" className="group flex flex-col px-6 py-4 hover:bg-blue-50 transition">
                    <span className="font-extrabold text-gray-800 group-hover:text-blue-900">Blockchain & Innovative Projects</span>
                    <span className="text-sm text-gray-500">Secure, decentralized and next-gen technology solutions</span>
                  </a>
                  <a href="/audits-and-consulting" className="group flex flex-col px-6 py-4 hover:bg-blue-50 transition">
                    <span className="font-extrabold text-gray-800 group-hover:text-blue-900">AI Audit & Consulting</span>
                    <span className="text-sm text-gray-500">Expert review, strategy and optimization for AI systems</span>
                  </a>
                </div>
              )}
            </div> */}

            <button
              onClick={() => setIsContactModalOpen(true)}
              className={`px-2 font-extrabold transition-colors duration-300 cursor-pointer ${
                !top ? 'text-gray-500 hover:text-blue-900' : 'text-white/90 hover:text-white'
              }`}
            >
              {t('nav.contact')}
            </button>

            <button
              type="button"
              onClick={openCalendly}
              disabled={!calendlyLoaded}
              className={`text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:bg-blue-800 inline-flex items-center justify-center px-6 py-3 shadow-xl rounded-xl transition-all duration-300 font-bold hover:scale-105 ${
                !calendlyLoaded ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              {t('nav.demo')}
            </button>

            <button
              onClick={toggleLanguage}
              className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-black border-2 transition-all duration-300 ${
                !top
                  ? 'border-blue-900 text-blue-900 hover:bg-blue-900 hover:text-white'
                  : 'border-white/40 text-white hover:bg-white hover:text-blue-900'
              }`}
            >
              <span className={i18n.language === 'en' ? 'opacity-100' : 'opacity-40'}>EN</span>
              <span className="opacity-40">|</span>
              <span className={i18n.language === 'fr' ? 'opacity-100' : 'opacity-40'}>FR</span>
            </button>
          </div>

          {/* Mobile Menu Button */}
          <button 
            className="p-2 rounded-lg lg:hidden text-blue-900 hover:bg-blue-50 transition-colors duration-200" 
            onClick={handleClick}
            aria-label="Toggle menu"
          >
            <svg
              className={`h-6 w-6 transition-colors duration-300 ${
                !top ? 'fill-blue-900' : 'fill-white'
              }`}
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
            >
              {isOpen ? (
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M18.278 16.864a1 1 0 0 1-1.414 1.414l-4.829-4.828-4.828 4.828a1 1 0 0 1-1.414-1.414l4.828-4.829-4.828-4.828a1 1 0 0 1 1.414-1.414l4.829 4.828 4.828-4.828a1 1 0 1 1 1.414 1.414l-4.828 4.829 4.828 4.828z"
                />
              ) : (
                <path
                  fillRule="evenodd"
                  d="M4 5h16a1 1 0 0 1 0 2H4a1 1 0 1 1 0-2zm0 6h16a1 1 0 0 1 0 2H4a1 1 0 0 1 0-2zm0 6h16a1 1 0 0 1 0 2H4a1 1 0 0 1 0-2z"
                />
              )}
            </svg>
          </button>
        </div>

        {/* Mobile Menu */}
        <div
          className={`lg:hidden transition-all duration-300 ease-in-out bg-white border-t border-gray-100 ${
            isOpen ? 'max-h-screen opacity-100' : 'max-h-0 opacity-0 overflow-hidden'
          }`}
        >
          <div className="flex flex-col px-6 py-8 space-y-6 font-extrabold text-blue-900">
            <HashLink 
              smooth 
              to="/#hero" 
              onClick={() => setIsOpen(false)}
              className="hover:text-blue-600 transition-colors"
            >
              {t('nav.home')}
            </HashLink>
            
            <HashLink 
              smooth 
              to="/#about" 
              onClick={() => setIsOpen(false)}
              className="hover:text-blue-600 transition-colors"
            >
              {t('nav.about')}
            </HashLink>
            
            <HashLink 
              smooth 
              to="/#services" 
              onClick={() => setIsOpen(false)}
              className="hover:text-blue-600 transition-colors"
            >
              {t('nav.services')}
            </HashLink>

            {/* Mobile Product Dropdown */}
            {/* <div className="flex flex-col space-y-2">
              <button
                onClick={() => setMobileProductOpen(!mobileProductOpen)}
                className="flex items-center justify-between hover:text-blue-600 transition-colors"
              >
                <span>{t('nav.product')}</span>
                <svg
                  className={`w-4 h-4 transition-transform duration-300 ${
                    mobileProductOpen ? 'rotate-180' : ''
                  }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {mobileProductOpen && (
                <div className="pl-4 space-y-3 pt-2">
                  <a 
                    href="/products/website-app" 
                    onClick={() => setIsOpen(false)}
                    className="block text-sm font-semibold text-gray-600 hover:text-blue-600 transition-colors"
                  >
                    Website & App Creation
                  </a>
                  <a 
                    href="/products/ai-solutions" 
                    onClick={() => setIsOpen(false)}
                    className="block text-sm font-semibold text-gray-600 hover:text-blue-600 transition-colors"
                  >
                    Custom AI Solutions
                  </a>
                  <a 
                    href="/products/blockchain" 
                    onClick={() => setIsOpen(false)}
                    className="block text-sm font-semibold text-gray-600 hover:text-blue-600 transition-colors"
                  >
                    Blockchain & Innovative Projects
                  </a>
                  <a 
                    href="/products/ai-audit" 
                    onClick={() => setIsOpen(false)}
                    className="block text-sm font-semibold text-gray-600 hover:text-blue-600 transition-colors"
                  >
                    AI Audit & Consulting
                  </a>
                </div>
              )}
            </div> */}

            <button 
              onClick={() => { 
                setIsContactModalOpen(true); 
                setIsOpen(false); 
              }} 
              className="text-left hover:text-blue-600 transition-colors"
            >
              {t('nav.contact')}
            </button>

            <button 
              type="button" 
              onClick={() => {
                openCalendly();
                setIsOpen(false);
              }}
              disabled={!calendlyLoaded} 
              className={`bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-4 px-6 rounded-xl text-center shadow-lg w-full font-bold hover:scale-105 transition-all ${
                !calendlyLoaded ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              {t('nav.demo')}
            </button>

            <button
              onClick={toggleLanguage}
              className="flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-xs font-black border-2 border-blue-900 text-blue-900 hover:bg-blue-900 hover:text-white transition-all duration-300"
            >
              <span className={i18n.language === 'en' ? 'opacity-100' : 'opacity-40'}>EN</span>
              <span className="opacity-40">|</span>
              <span className={i18n.language === 'fr' ? 'opacity-100' : 'opacity-40'}>FR</span>
            </button>
          </div>
        </div>
      </nav>

      <ContactModal isOpen={isContactModalOpen} onClose={() => setIsContactModalOpen(false)} />
    </>
  );
};

export default NavBar;